/**
 * HarnessesBrowser — monitor harn-service (lexruntime harness execution sidecar).
 *
 * Shows:
 * - Mode toggle (Gateway via runtime / Direct via host standalone)
 * - Health card + task queue stats
 * - Harness catalogue (from GET /harnesses)
 * - Task table with filters (status, harnessId) and 2s polling
 * - Expandable row: full task record + result/error + cancel button
 *
 * WebSocket streaming:
 * - Direct mode: live progress + llm_stream + log frames via /harn-direct-ws
 *   Vite proxy. "Run selftest" button triggers a task and renders phases live.
 * - Gateway mode: REST polling only — the openclaw plugin proxy returns 501 on
 *   WS upgrade (see harnesses/proxy.ts Q4/Q5 open questions).
 */

import {
  Ban,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Info,
  Loader2,
  PackageSearch,
  Play,
  RefreshCw,
  Radio,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useHarnClient } from "@/hooks/useHarnClient";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { HarnApiError } from "@/lib/harn-client";
import { findChainRoot, walkChainDescendants, type ChainNode } from "@/lib/harn-chain";
import { HarnessRunnerPanel } from "@/components/harnesses/HarnessRunnerPanel";
import type {
  ErrorFrame,
  HarnessManifestSummary,
  HarnHealth,
  HarnTaskRecord,
  HarnTaskSummary,
  ResultFrame,
} from "@/types/harn";

// §19.1 — steps are NO LONGER hardcoded (was the selftest-specific
// PHASE_ORDER). The live-stream panel collects them dynamically from
// incoming `progress` frames, in first-seen order, so the panel renders
// for ANY harness the runner starts.
type PhaseState = "running" | "done";
interface LiveStep {
  step: string;
  state: PhaseState;
}

const POLL_INTERVAL_MS = 2_000;

function formatDurationMs(ms: number | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function formatRelative(tsMs: number | undefined): string {
  if (tsMs == null) return "—";
  const delta = Date.now() - tsMs;
  if (delta < 5_000) return "just now";
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(tsMs).toLocaleString();
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    queued: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    running: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    awaiting_user: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };
  const cls = colors[status] ?? colors.queued;
  const label = status === "awaiting_user" ? "awaiting user" : status;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

export function HarnessesBrowser() {
  const { client, unavailableReason, mode, setMode } = useHarnClient();
  const runtime = useRuntimeContext();

  const [health, setHealth] = useState<HarnHealth | null>(null);
  const [harnesses, setHarnesses] = useState<HarnessManifestSummary[]>([]);
  const [tasks, setTasks] = useState<HarnTaskSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [harnessFilter, setHarnessFilter] = useState("");

  // Expanded row
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<HarnTaskRecord | null>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState<string | null>(null);

  // §18.5c chain timeline — multi-hop awaiting-user resume chain.
  // Loads on demand: walks parentTaskId up to find the root, then
  // walks `?parent=<id>` descendants breadth-first. Cache key is the
  // originally-clicked task id (`originTaskId`); we keep the cache
  // alive across expanded-row navigation as long as the next row is
  // *also* a node in the current chain (it's the same chain).
  const [chainState, setChainState] = useState<{
    originTaskId: string;        // the originally-clicked task — used to highlight it in the tree
    nodes: ChainNode[];
    truncated: boolean;
  } | null>(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [chainError, setChainError] = useState<string | null>(null);
  // Ignore-flag for racy chain walks: when the user expands row B
  // mid-walk on row A, A's setChainState should be a no-op.
  const chainOriginRef = useRef<string | null>(null);

  // Live stream panel — counters only, no unbounded frame array
  const [streamTaskId, setStreamTaskId] = useState<string | null>(null);
  const [streamText, setStreamText] = useState("");
  const [streamRunning, setStreamRunning] = useState(false);
  const [streamTerminal, setStreamTerminal] = useState<ResultFrame | ErrorFrame | null>(null);
  const [frameCounts, setFrameCounts] = useState<Record<string, number>>({});
  const [liveSteps, setLiveSteps] = useState<LiveStep[]>([]);
  const unsubRef = useRef<(() => void) | null>(null);

  // §19.1 — the runner panel starts the task; this subscribes the
  // live-stream panel to it. Steps are collected dynamically from
  // `progress` frames (first-seen order) — no harness-specific
  // hardcoding. Works for any harness, not just selftest.
  const handleTaskStarted = useCallback(
    (taskId: string) => {
      if (!client) return;
      setStreamText("");
      setStreamTerminal(null);
      setFrameCounts({});
      setLiveSteps([]);
      setError(null);
      // Tear down any previous subscription
      unsubRef.current?.();
      unsubRef.current = null;

      setStreamTaskId(taskId);
      setStreamRunning(true);

      if (!client.streamingSupported) {
        // Gateway mode: no WS — task still appears in the table via 2s
        // polling; the live panel just won't animate per-frame.
        setStreamRunning(false);
        return;
      }

      const unsub = client.streamTask(
        taskId,
        (frame) => {
          setFrameCounts((prev) => ({ ...prev, [frame.type]: (prev[frame.type] ?? 0) + 1 }));
          if (frame.type === "progress") {
            if (frame.step !== "done") {
              setLiveSteps((prev) => {
                // mark any running step done; append the new step if unseen
                const next = prev.map((s) =>
                  s.state === "running" ? { ...s, state: "done" as const } : s,
                );
                if (!next.some((s) => s.step === frame.step)) {
                  next.push({ step: frame.step, state: "running" });
                } else {
                  return next.map((s) =>
                    s.step === frame.step ? { ...s, state: "running" as const } : s,
                  );
                }
                return next;
              });
            }
          } else if (frame.type === "llm_stream") {
            const ev = frame.event;
            if (ev?.type === "text_delta" && typeof ev.delta === "string") {
              setStreamText((prev) => prev + ev.delta);
            }
          } else if (frame.type === "result" || frame.type === "error") {
            setStreamTerminal(frame);
            setStreamRunning(false);
            setLiveSteps((prev) =>
              prev.map((s) => (s.state === "running" ? { ...s, state: "done" as const } : s)),
            );
          }
        },
        (code) => {
          setStreamRunning(false);
          if (code !== 1000) {
            setError(`WebSocket disconnected unexpectedly (code ${code})`);
          }
        },
        (err) => {
          setStreamRunning(false);
          setError(`WebSocket error: ${err.message}`);
        },
      );
      unsubRef.current = unsub;
    },
    [client],
  );

  // Unsubscribe on unmount
  useEffect(() => {
    return () => {
      unsubRef.current?.();
    };
  }, []);

  const reload = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!client) return;
      const silent = opts?.silent ?? false;
      try {
        if (!silent) setLoading(true);
        const [h, hs, ts] = await Promise.all([
          client.getHealth().catch(() => null),
          client.listHarnesses().catch(() => ({ harnesses: [] })),
          client.listTasks({
            status: statusFilter || undefined,
            harnessId: harnessFilter || undefined,
            limit: 100,
          }),
        ]);
        setHealth(h);
        setHarnesses(hs.harnesses);
        setTasks(ts.tasks);
        setError(null);
      } catch (e) {
        const msg =
          e instanceof HarnApiError
            ? `${e.status}: ${e.body.slice(0, 300)}`
            : e instanceof Error
              ? e.message
              : "Failed to load harnesses";
        setError(msg);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [client, statusFilter, harnessFilter],
  );

  // Initial load + silent polling (background refresh doesn't blink the spinner)
  useEffect(() => {
    if (!client) return;
    reload();
    const id = setInterval(() => reload({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [client, reload]);

  // When expanded row is set, fetch full detail
  useEffect(() => {
    if (!client || !expandedTaskId) {
      setExpandedDetail(null);
      return;
    }
    setExpandedLoading(true);
    client
      .getTask(expandedTaskId)
      .then(setExpandedDetail)
      .catch(() => setExpandedDetail(null))
      .finally(() => setExpandedLoading(false));
  }, [client, expandedTaskId, tasks]);

  const handleCancel = useCallback(
    async (taskId: string) => {
      if (!client) return;
      setCancelLoading(taskId);
      try {
        await client.cancelTask(taskId);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to cancel");
      } finally {
        setCancelLoading(null);
      }
    },
    [client, reload],
  );

  const loadChain = useCallback(
    async (taskId: string) => {
      if (!client) return;
      // Stamp this walk so a later loadChain can invalidate stale results.
      chainOriginRef.current = taskId;
      setChainLoading(true);
      setChainError(null);
      try {
        // 1. Find root by walking parentTaskId upward.
        const rootId = await findChainRoot(taskId, async (id) => {
          const t = await client.getTask(id);
          return { taskId: t.taskId, parentTaskId: t.parentTaskId };
        });
        // 2. Walk descendants from root breadth-first.
        const result = await walkChainDescendants(
          rootId,
          async (id) => client.getTask(id),
          async (parentId) => {
            const res = await client.listTasks({ parent: parentId, limit: 100 });
            return res.tasks;
          },
        );
        // Discard the result if a newer load took over while we were walking.
        if (chainOriginRef.current !== taskId) return;
        setChainState({ originTaskId: taskId, nodes: result.nodes, truncated: result.truncated });
      } catch (e) {
        if (chainOriginRef.current !== taskId) return;
        setChainError(e instanceof Error ? e.message : "Failed to load chain");
      } finally {
        if (chainOriginRef.current === taskId) setChainLoading(false);
      }
    },
    [client],
  );

  // When the expanded row changes, drop the chain only if the new task isn't
  // already part of the cached chain — otherwise navigating between sibling
  // chain nodes would lose the timeline view the user just loaded.
  useEffect(() => {
    if (!expandedTaskId) {
      setChainState(null);
      setChainError(null);
      setChainLoading(false);
      chainOriginRef.current = null;
      return;
    }
    setChainState((prev) => {
      if (!prev) return prev;
      const stillRelevant = prev.nodes.some((n) => n.task.taskId === expandedTaskId);
      if (stillRelevant) return prev;
      // Different chain — drop and let the user re-load explicitly.
      setChainError(null);
      setChainLoading(false);
      chainOriginRef.current = null;
      return null;
    });
  }, [expandedTaskId]);

  const harnessOptions = useMemo(() => harnesses.map((h) => h.id), [harnesses]);

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Info className="mb-3 h-10 w-10 text-slate-400" strokeWidth={1.5} />
        <p className="text-sm text-slate-500 dark:text-slate-400">{unavailableReason}</p>
        <button
          type="button"
          onClick={() => setMode("direct")}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          Switch to Direct mode (host standalone)
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header: mode toggle + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-indigo-500" />
          <h2 className="text-lg font-semibold">Harnesses</h2>
          <span className="text-xs text-slate-400">· {client.modeLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-slate-200 p-0.5 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setMode("gateway")}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === "gateway"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              Gateway
            </button>
            <button
              type="button"
              onClick={() => setMode("direct")}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === "direct"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              Direct
            </button>
          </div>
          <button
            type="button"
            onClick={() => reload()}
            disabled={loading}
            title="Refresh"
            className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Health + queue stats */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase text-muted-foreground">
              Service Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health ? (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-green-500">{health.status}</span>
                <span className="text-xs text-muted-foreground">
                  · {health.harnesses} harness{health.harnesses === 1 ? "" : "es"}
                </span>
              </div>
            ) : (
              <span className="text-sm text-slate-400">—</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase text-muted-foreground">
              Task Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health ? (
              <div className="flex items-baseline gap-3 text-sm">
                <span className="text-2xl font-semibold tabular-nums">{health.tasks.active}</span>
                <span className="text-xs text-muted-foreground">running</span>
                <span className="text-xs text-slate-400">|</span>
                <span className="text-xs text-slate-500">queued {health.tasks.queued}</span>
              </div>
            ) : (
              <span className="text-sm text-slate-400">—</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase text-muted-foreground">
              Task History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health ? (
              <div className="flex items-baseline gap-3 text-sm">
                <span className="text-green-500">{health.tasks.completed} done</span>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-red-500">{health.tasks.failed} failed</span>
              </div>
            ) : (
              <span className="text-sm text-slate-400">—</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Catalogue */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <PackageSearch className="h-3.5 w-3.5" />
            Catalogue ({harnesses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {harnesses.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">No harnesses registered</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  <th className="pb-2 pr-4">ID</th>
                  <th className="pb-2 pr-4">Version</th>
                  <th className="pb-2 pr-4">Scope</th>
                  <th className="pb-2 pr-4">Tool</th>
                  <th className="pb-2 pr-4">Model</th>
                  <th className="pb-2">Permission</th>
                </tr>
              </thead>
              <tbody>
                {harnesses.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-4 font-mono text-xs font-medium">{h.id}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-500">{h.version}</td>
                    <td className="py-2 pr-4">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium dark:bg-slate-800">
                        {h.scope}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-xs">{h.tool.label}</td>
                    <td className="py-2 pr-4 font-mono text-[10px] text-slate-500">
                      {h.model.provider}/{h.model.id}
                    </td>
                    <td className="py-2">
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        {h.tool.permission_group}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* §19.1 — Harness Runner (catalogue-driven) + Live Stream */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Radio
                className={`h-3.5 w-3.5 ${streamRunning ? "text-red-500 animate-pulse" : "text-slate-400"}`}
              />
              <span>Harness Runner</span>
              {!client.streamingSupported && (
                <span className="text-[10px] font-normal text-amber-500">
                  (gateway WS proxy not implemented — live frames in Direct mode)
                </span>
              )}
              {streamTaskId && (
                <span className="text-[10px] font-mono font-normal text-slate-500">
                  · {streamTaskId.slice(0, 16)}
                </span>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <HarnessRunnerPanel
            client={client}
            harnesses={harnesses}
            topologyOrgId={
              client.target.kind === "gateway" && runtime.target
                ? runtime.target.orgId
                : "org-dev-001"
            }
            onTaskStarted={handleTaskStarted}
          />

          {Object.keys(frameCounts).length === 0 && !streamRunning ? (
            <p className="border-t border-slate-100 py-4 text-center text-xs text-slate-400 dark:border-slate-800">
              Select a harness and click "Run harness" to watch live WS frames.
            </p>
          ) : (
            <div className="space-y-3 border-t border-slate-100 pt-3 dark:border-slate-800">
              {/* Dynamic step list — collected from progress frames (§19.1) */}
              {liveSteps.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {liveSteps.map(({ step, state }) => {
                    const cls =
                      state === "done"
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 animate-pulse";
                    return (
                      <div
                        key={step}
                        className={`rounded px-1.5 py-1 text-center text-[10px] font-medium transition-colors ${cls}`}
                      >
                        {step}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Live LLM output */}
              {streamText && (
                <div>
                  <div className="mb-1 text-[10px] uppercase text-slate-400">LLM output (live)</div>
                  <div className="rounded bg-slate-50 p-2 font-mono text-xs leading-relaxed text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                    {streamText}
                    {streamRunning && (
                      <span className="inline-block h-3 w-1.5 animate-pulse bg-slate-500 align-middle" />
                    )}
                  </div>
                </div>
              )}

              {/* Frame counts summary (from counter state, not array length) */}
              <div className="flex items-center gap-3 text-[10px] text-slate-500">
                <span>Frames:</span>
                {(["progress", "log", "llm_stream", "result", "error"] as const).map((type) => {
                  const n = frameCounts[type] ?? 0;
                  if (n === 0) return null;
                  return (
                    <span key={type} className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">
                      {type}: <span className="font-semibold tabular-nums">{n}</span>
                    </span>
                  );
                })}
              </div>

              {/* Terminal frame — inspect phase summary for soft-fail detection */}
              {streamTerminal && streamTerminal.type === "result" && (() => {
                const summary = (streamTerminal.data as { summary?: { passed?: number; failed?: number; skipped?: number; total?: number } } | undefined)?.summary;
                const failed = summary?.failed ?? 0;
                const passed = summary?.passed ?? 0;
                const skipped = summary?.skipped ?? 0;
                if (failed > 0) {
                  return (
                    <div className="rounded bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                      ⚠ Task completed with soft failures — {passed} passed · {failed} failed · {skipped} skipped
                    </div>
                  );
                }
                return (
                  <div className="rounded bg-green-50 p-2 text-xs text-green-700 dark:bg-green-900/20 dark:text-green-400">
                    ✓ Task completed successfully{summary ? ` — ${passed} passed · ${skipped} skipped` : ""}
                  </div>
                );
              })()}
              {streamTerminal && streamTerminal.type === "error" && (
                <div className="rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  ✗ {streamTerminal.code}: {streamTerminal.message}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
            <span>Tasks ({tasks.length})</span>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setExpandedTaskId(null);
                }}
                className="rounded border border-slate-200 bg-transparent px-2 py-0.5 text-xs dark:border-slate-700"
              >
                <option value="">All statuses</option>
                <option value="queued">Queued</option>
                <option value="running">Running</option>
                <option value="awaiting_user">Awaiting user</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={harnessFilter}
                onChange={(e) => {
                  setHarnessFilter(e.target.value);
                  setExpandedTaskId(null);
                }}
                className="rounded border border-slate-200 bg-transparent px-2 py-0.5 text-xs dark:border-slate-700"
              >
                <option value="">All harnesses</option>
                {harnessOptions.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No tasks match the current filters</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {tasks.map((t) => {
                const expanded = expandedTaskId === t.taskId;
                return (
                  <div key={t.taskId}>
                    <button
                      type="button"
                      onClick={() => setExpandedTaskId(expanded ? null : t.taskId)}
                      className="flex w-full items-center gap-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-900"
                    >
                      {expanded ? (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      )}
                      <span className="font-mono text-[10px] text-slate-500 w-24 shrink-0 truncate">
                        {t.taskId.slice(0, 12)}
                      </span>
                      <span className="text-xs font-medium w-32 shrink-0 truncate">{t.harnessId}</span>
                      <StatusBadge status={t.status} />
                      <span className="flex-1 text-xs text-slate-500 truncate">
                        {t.progress ? `${t.progress.step} · ${t.progress.pct}%` : "—"}
                      </span>
                      <span className="shrink-0 text-[10px] text-slate-400">
                        {formatRelative(t.createdAt)}
                      </span>
                    </button>
                    {expanded && (
                      <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-xs dark:border-slate-800 dark:bg-slate-900/40">
                        {expandedLoading ? (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading details…
                          </div>
                        ) : expandedDetail ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              <div>
                                <span className="text-slate-400">Task ID:</span>{" "}
                                <span className="font-mono">{expandedDetail.taskId}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">Scope:</span> {expandedDetail.scope}
                              </div>
                              <div>
                                <span className="text-slate-400">Duration:</span>{" "}
                                {formatDurationMs(expandedDetail.durationMs)}
                              </div>
                              <div>
                                <span className="text-slate-400">Created:</span>{" "}
                                {new Date(expandedDetail.createdAt).toLocaleTimeString()}
                              </div>
                              {expandedDetail.orgId && (
                                <div>
                                  <span className="text-slate-400">Org:</span> {expandedDetail.orgId}
                                </div>
                              )}
                              {expandedDetail.clientId && (
                                <div>
                                  <span className="text-slate-400">Client:</span>{" "}
                                  {expandedDetail.clientId}
                                </div>
                              )}
                              {expandedDetail.matterId && (
                                <div>
                                  <span className="text-slate-400">Matter:</span>{" "}
                                  {expandedDetail.matterId}
                                </div>
                              )}
                              {expandedDetail.caseId && (
                                <div>
                                  <span className="text-slate-400">Case:</span>{" "}
                                  {expandedDetail.caseId}
                                </div>
                              )}
                              {expandedDetail.parentTaskId && (
                                <div>
                                  <span className="text-slate-400">Parent:</span>{" "}
                                  <span
                                    className="cursor-pointer font-mono underline"
                                    title="Click to view the parent task — clears the status filter so the parent is visible regardless of its terminal state."
                                    onClick={() => {
                                      // Parents in a resumed chain usually
                                      // sit in `completed/`, so filtering by
                                      // a single status (e.g. awaiting_user)
                                      // would hide the parent row. Clear it
                                      // before navigating.
                                      setStatusFilter("");
                                      setExpandedTaskId(expandedDetail.parentTaskId!);
                                    }}
                                  >
                                    {expandedDetail.parentTaskId.slice(0, 8)}…
                                  </span>
                                  {expandedDetail.chainDepth !== undefined && (
                                    <span className="ml-2 text-slate-400">
                                      (depth {expandedDetail.chainDepth})
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            {expandedDetail.error && (
                              <div className="rounded bg-red-50 p-2 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                                <div className="font-semibold">{expandedDetail.error.code}</div>
                                <div className="mt-0.5">{expandedDetail.error.message}</div>
                              </div>
                            )}

                            {expandedDetail.result != null && (
                              <div>
                                <div className="text-slate-400">Result:</div>
                                <pre className="mt-1 max-h-60 overflow-auto rounded bg-white p-2 text-[10px] dark:bg-slate-800">
                                  {JSON.stringify(expandedDetail.result, null, 2)}
                                </pre>
                              </div>
                            )}

                            {expandedDetail.status === "awaiting_user" && expandedDetail.awaitingUser && (
                              <div className="space-y-1 pt-2">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                                  Awaiting user —{" "}
                                  {expandedDetail.awaitingUser.questions.length}{" "}
                                  question
                                  {expandedDetail.awaitingUser.questions.length === 1 ? "" : "s"}
                                </div>
                                <div className="rounded bg-amber-50 p-2 font-mono text-[10px] text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                                  <div className="mb-1 text-[9px] opacity-60">
                                    resume_token:{" "}
                                    {expandedDetail.awaitingUser.resumeToken.slice(0, 16)}
                                    … · expires in{" "}
                                    {Math.max(
                                      0,
                                      Math.round(
                                        (expandedDetail.awaitingUser.expiresAt -
                                          Date.now()) /
                                          1000,
                                      ),
                                    )}
                                    s
                                  </div>
                                  {expandedDetail.awaitingUser.questions.map((q) => (
                                    <div key={q.question_id} className="py-0.5">
                                      <span className="opacity-60">
                                        [{q.question_id}]
                                      </span>{" "}
                                      {q.prompt}
                                      {q.choices && q.choices.length > 0 && (
                                        <span className="opacity-60">
                                          {" "}
                                          · choices:{" "}
                                          {q.choices.map((c) => String(c)).join(", ")}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-[10px] text-slate-400">
                                  Read-only view — LexPortal admins can only
                                  cancel pending chains. Answering requires
                                  the client UI (LexIDE) to POST back with
                                  the resume token.
                                </p>
                              </div>
                            )}

                            {(expandedDetail.status === "queued" ||
                              expandedDetail.status === "running" ||
                              expandedDetail.status === "awaiting_user") && (
                              <div className="pt-1">
                                <button
                                  type="button"
                                  onClick={() => handleCancel(expandedDetail.taskId)}
                                  disabled={cancelLoading === expandedDetail.taskId}
                                  className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-900/20"
                                >
                                  {cancelLoading === expandedDetail.taskId ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Ban className="h-3 w-3" />
                                  )}
                                  {expandedDetail.status === "awaiting_user"
                                    ? "Cancel awaiting-user"
                                    : "Cancel task"}
                                </button>
                              </div>
                            )}

                            {/* §18.5c chain timeline — multi-hop awaiting-user resume tree.
                                Lazy-loaded; cached per-row until the expanded row changes. */}
                            <div className="pt-2" data-testid="chain-section">
                              {!chainState && !chainLoading && (
                                <button
                                  type="button"
                                  onClick={() => loadChain(expandedDetail.taskId)}
                                  className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                                  data-testid="chain-load-button"
                                >
                                  <GitBranch className="h-3 w-3" />
                                  Show chain timeline
                                </button>
                              )}
                              {chainLoading && (
                                <div className="flex items-center gap-2 text-slate-400">
                                  <Loader2 className="h-3 w-3 animate-spin" /> Walking chain…
                                </div>
                              )}
                              {chainError && (
                                <div className="rounded bg-red-50 p-2 text-[10px] text-red-700 dark:bg-red-900/20 dark:text-red-400">
                                  Chain walk failed: {chainError}
                                </div>
                              )}
                              {chainState && (
                                <div className="space-y-1.5" data-testid="chain-tree">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                      <GitBranch className="h-3 w-3" />
                                      Chain timeline
                                      <span className="font-normal lowercase text-slate-400">
                                        · {chainState.nodes.length} task{chainState.nodes.length === 1 ? "" : "s"}
                                        {chainState.truncated && " (truncated at depth 8)"}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => loadChain(expandedDetail.taskId)}
                                      title="Refresh chain"
                                      className="rounded p-0.5 text-slate-400 hover:text-slate-600"
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </button>
                                  </div>
                                  <div className="rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                                    {chainState.nodes.map((node) => {
                                      // Highlight the row that's currently expanded — when the
                                      // user navigates between chain nodes via click, the
                                      // highlight follows.
                                      const isCurrent = node.task.taskId === expandedTaskId;
                                      return (
                                        <button
                                          key={node.task.taskId}
                                          type="button"
                                          onClick={() => {
                                            // Navigate to that task — clear filter so any status is visible.
                                            if (node.task.taskId === expandedTaskId) return;
                                            setStatusFilter("");
                                            setExpandedTaskId(node.task.taskId);
                                          }}
                                          className={`flex w-full items-center gap-2 border-b border-slate-100 px-2 py-1.5 text-left text-[10px] last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
                                            isCurrent ? "bg-indigo-50 dark:bg-indigo-900/20" : ""
                                          }`}
                                          data-testid={`chain-node-${node.task.taskId}`}
                                        >
                                          <span
                                            className="font-mono text-slate-400"
                                            style={{ paddingLeft: `${node.depth * 12}px` }}
                                          >
                                            {node.depth === 0 ? "●" : "└"}
                                          </span>
                                          <StatusBadge status={node.task.status} />
                                          <span className="font-mono text-slate-500">
                                            {node.task.taskId.slice(0, 12)}
                                          </span>
                                          <span className="truncate text-slate-600 dark:text-slate-400">
                                            {node.task.harnessId}
                                          </span>
                                          {node.task.progress && (
                                            <span className="ml-auto shrink-0 text-slate-400">
                                              {node.task.progress.step} · {node.task.progress.pct}%
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <p className="text-[9px] text-slate-400">
                                    Click any node to inspect that task. Root sits at depth 0;
                                    each resume hop adds one level.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-slate-400">Could not load task detail</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
